import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Eyebrow, MaskedLines, Reveal } from "../motion/Reveal";
import { care } from "../../content/site";

const Panel = ({ c, i, active, setActive }) => {
  const on = active === i;
  return (
    <button type="button" onMouseEnter={() => setActive(i)} onFocus={() => setActive(i)} onClick={() => setActive(i)}
      aria-pressed={on} data-testid={`care-panel-${c.word.toLowerCase()}`}
      className={`tc-care-panel group relative flex h-full min-h-[300px] flex-col overflow-hidden rounded-[24px] border p-6 text-left transition-[transform,border-color,background-color,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] sm:p-8 lg:min-h-[460px] ${on ? "-translate-y-1.5 border-orange-500/60 bg-[#1A1411] shadow-[0_30px_70px_-35px_rgba(234,88,12,0.55)]" : "border-white/10 bg-[#120F0D] hover:border-orange-500/30"}`}>
      <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-400 to-transparent transition-opacity duration-500 ${on ? "opacity-100" : "opacity-0"}`} />
      <span className={`pointer-events-none absolute -bottom-12 -right-3 select-none font-display text-[12rem] font-semibold leading-none transition-[color,transform] duration-700 lg:text-[14rem] ${on ? "-translate-y-2 text-orange-500/[0.14]" : "text-white/[0.035]"}`} aria-hidden="true">
        {c.letter}
      </span>
      <span className="relative flex items-center justify-between">
        <span className="font-mono text-xs text-orange-400">0{i + 1} / 04</span>
        <span className={`h-2 w-2 rounded-full transition-[background-color,box-shadow] duration-500 ${on ? "bg-orange-500 shadow-[0_0_12px_2px_rgba(249,115,22,0.55)]" : "bg-white/20"}`} />
      </span>
      <span className="relative mt-10 flex flex-1 flex-col">
        <span className={`block font-display text-6xl font-semibold leading-none transition-colors duration-500 ${on ? "text-orange-400" : "text-orange-500"}`}>{c.letter}</span>
        <span className="mt-5 block font-display text-[1.6rem] leading-tight text-stone-50">{c.word}</span>
        <span className={`mt-4 block h-px bg-orange-500/60 transition-[width] duration-700 ${on ? "w-16" : "w-8"}`} />
        <span className="mt-4 block text-[15px] leading-relaxed text-stone-100">{c.line}</span>
        <span className="mt-auto block pt-6 text-sm leading-relaxed text-stone-300">{c.detail}</span>
      </span>
    </button>
  );
};

export const OurName = () => {
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section id="our-name" onMouseMove={onMove} data-testid="our-name-section"
      className="tc-spotlight relative overflow-hidden bg-[#080605] py-24 text-stone-100 sm:py-36">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <Eyebrow>04 — Our Name</Eyebrow>
        <h2 className="mt-10 font-display font-semibold leading-[0.88] tracking-[-0.03em] text-[clamp(3rem,12vw,11rem)]" aria-label="TECH plus C.A.R.E.">
          <span className="flex flex-wrap items-baseline gap-x-[0.18em]">
            <motion.span initial={reduce ? false : { opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="text-stone-50">TECH</motion.span>
            <span className="text-stone-500">+</span>
            <span className="flex">
              {"C.A.R.E.".split("").map((ch, i) => (
                <motion.span key={i} initial={reduce ? false : { opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.9, delay: 0.2 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  className={ch === "." ? "text-stone-500" : "tc-copper-text"}>{ch}</motion.span>
              ))}
            </span>
          </span>
        </h2>

        <div className="mt-14 grid gap-10 border-t border-white/10 pt-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-stone-400">TECH</p>
            <p className="mt-4 text-lg leading-relaxed text-stone-200 sm:text-xl">
              The technology, engineering, intelligence and innovation behind what we do.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-stone-400">C.A.R.E.</p>
            <p className="mt-4 text-lg leading-relaxed text-stone-200 sm:text-xl">
              Four commitments that shape how we work with every client. Each one is always readable — hover, tap or tab to highlight it.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="care-panels">
          {care.map((c, i) => <Panel key={c.letter} c={c} i={i} active={active} setActive={setActive} />)}
        </div>

        <div className="mt-24 sm:mt-32">
          <p className="font-display text-3xl font-medium leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl" data-testid="care-closing">
            <MaskedLines inView stagger={0.15} lines={["Technology is what we build.", <span key="c" className="tc-copper-text">Care is how we build it.</span>]} />
          </p>
        </div>
      </div>
    </section>
  );
};
